import {
	EVENT_TYPES,
	type ActionGraph,
} from "./actions.ts";
import {
	ACTION_SPECS,
	isDataProducer,
} from "./schemaSpec.ts";

export type GraphLayoutDirection = "vertical" | "horizontal";

export type GraphLayoutPosition = {
	x: number;
	y: number;
};

export const GRAPH_LAYOUT_NODE_WIDTH = 320;
export const GRAPH_LAYOUT_EVENT_NODE_WIDTH = 220;
export const GRAPH_LAYOUT_DATA_NODE_WIDTH = 280;
export const GRAPH_LAYOUT_NODE_HEIGHT = 100;
export const GRAPH_LAYOUT_LAYER_GAP = 90;
export const GRAPH_LAYOUT_BRANCH_GAP = 80;
export const GRAPH_LAYOUT_COMPONENT_GAP = 160;

const COMMENT_PADDING = 40;
const COMMENT_HEADER_HEIGHT = 40;
const DATA_INPUT_GAP = 80;
const DATA_INPUT_STACK_GAP = 40;

type GraphNode = ActionGraph["nodes"][string];

type LayoutEdge = {
	source: string;
	target: string;
	sourceHandle: string;
	index: number;
};

type DataInputEdge = {
	source: string;
	target: string;
	port: string;
	index: number;
};

type NodeWithInputs = {
	inputs?: Record<string, { sourceNodeId: string; sourcePort: string }>;
};

type GraphLayoutOptions = {
	direction?: GraphLayoutDirection;
	origin?: GraphLayoutPosition;
	layoutComments?: boolean;
};

const EVENT_ORDER = new Map<string, number>(
	EVENT_TYPES.map((type, index) => [type, index]),
);

const HANDLE_ORDER = new Map<string, number>([
	["Next", 0],
	["Success", 1],
	["Succeeded", 2],
	["InProgress", 3],
	["Arrived", 4],
	["True", 5],
	["False", 6],
	["Failed", 7],
	["Error", 8],
	["Cancelled", 9],
	["NotStarted", 10],
]);

const DATA_NODE_TYPES = new Set<string>(
	ACTION_SPECS.filter(isDataProducer).map((spec) => spec.type),
);

function nodePosition(node: GraphNode): GraphLayoutPosition {
	return node.position ?? { x: 0, y: 0 };
}

function nodeWidth(node: GraphNode): number {
	if (EVENT_ORDER.has(node.type)) return GRAPH_LAYOUT_EVENT_NODE_WIDTH;
	if (DATA_NODE_TYPES.has(node.type)) return GRAPH_LAYOUT_DATA_NODE_WIDTH;
	return GRAPH_LAYOUT_NODE_WIDTH;
}

function nodeHeight(_node: GraphNode): number {
	return GRAPH_LAYOUT_NODE_HEIGHT;
}

function nodeBreadth(graph: ActionGraph, nodeId: string, direction: GraphLayoutDirection): number {
	const node = graph.nodes[nodeId];
	if (!node) return direction === "vertical" ? GRAPH_LAYOUT_NODE_WIDTH : GRAPH_LAYOUT_NODE_HEIGHT;
	return direction === "vertical" ? nodeWidth(node) : nodeHeight(node);
}

function compareNodeIds(graph: ActionGraph, a: string, b: string): number {
	const nodeA = graph.nodes[a];
	const nodeB = graph.nodes[b];
	if (!nodeA || !nodeB) return a.localeCompare(b);
	const eventA = EVENT_ORDER.get(nodeA.type) ?? Number.POSITIVE_INFINITY;
	const eventB = EVENT_ORDER.get(nodeB.type) ?? Number.POSITIVE_INFINITY;
	if (eventA !== eventB) return eventA - eventB;
	const posA = nodePosition(nodeA);
	const posB = nodePosition(nodeB);
	if (posA.y !== posB.y) return posA.y - posB.y;
	if (posA.x !== posB.x) return posA.x - posB.x;
	return a.localeCompare(b);
}

function compareEdges(graph: ActionGraph, a: LayoutEdge, b: LayoutEdge): number {
	const rankA = HANDLE_ORDER.get(a.sourceHandle) ?? 100 + a.index;
	const rankB = HANDLE_ORDER.get(b.sourceHandle) ?? 100 + b.index;
	if (rankA !== rankB) return rankA - rankB;
	return compareNodeIds(graph, a.target, b.target);
}

function buildLayoutEdges(graph: ActionGraph): LayoutEdge[] {
	const edges: LayoutEdge[] = [];
	graph.edges.forEach((edge, index) => {
		if (!graph.nodes[edge.source] || !graph.nodes[edge.target]) return;
		if (edge.source === edge.target) return;
		edges.push({
			source: edge.source,
			target: edge.target,
			sourceHandle: edge.sourceHandle,
			index,
		});
	});
	return edges;
}

function buildDataInputEdges(graph: ActionGraph): DataInputEdge[] {
	const edges: DataInputEdge[] = [];
	let index = 0;
	for (const [target, node] of Object.entries(graph.nodes)) {
		const inputs = (node as NodeWithInputs).inputs;
		if (!inputs) continue;
		for (const [port, binding] of Object.entries(inputs)) {
			if (!graph.nodes[binding.sourceNodeId] || binding.sourceNodeId === target) continue;
			edges.push({
				source: binding.sourceNodeId,
				target,
				port,
				index,
			});
			index += 1;
		}
	}
	return edges;
}

function normalizePositions(
	positions: Record<string, GraphLayoutPosition>,
	origin: GraphLayoutPosition,
) {
	let minX = Number.POSITIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	for (const position of Object.values(positions)) {
		minX = Math.min(minX, position.x);
		minY = Math.min(minY, position.y);
	}
	const dx = minX < origin.x ? origin.x - minX : 0;
	const dy = minY < origin.y ? origin.y - minY : 0;
	if (dx === 0 && dy === 0) return;
	for (const position of Object.values(positions)) {
		position.x = Math.round(position.x + dx);
		position.y = Math.round(position.y + dy);
	}
}

function placeDataInputs(
	graph: ActionGraph,
	positions: Record<string, GraphLayoutPosition>,
	layoutEdges: LayoutEdge[],
	direction: GraphLayoutDirection,
) {
	const dataEdges = buildDataInputEdges(graph);
	if (dataEdges.length === 0) return;

	const flowConnected = new Set<string>();
	for (const edge of layoutEdges) {
		flowConnected.add(edge.source);
		flowConnected.add(edge.target);
	}
	for (const [nodeId, node] of Object.entries(graph.nodes)) {
		if (EVENT_ORDER.has(node.type)) flowConnected.add(nodeId);
	}

	const inputsByTarget = new Map<string, DataInputEdge[]>();
	for (const edge of dataEdges) {
		const edges = inputsByTarget.get(edge.target) ?? [];
		edges.push(edge);
		inputsByTarget.set(edge.target, edges);
	}
	for (const edges of inputsByTarget.values()) {
		edges.sort((a, b) => {
			if (a.port !== b.port) return a.port.localeCompare(b.port);
			return a.index - b.index;
		});
	}

	const targets = Object.keys(graph.nodes)
		.filter((nodeId) => inputsByTarget.has(nodeId))
		.sort((a, b) => {
			const flowRankA = flowConnected.has(a) ? 0 : 1;
			const flowRankB = flowConnected.has(b) ? 0 : 1;
			if (flowRankA !== flowRankB) return flowRankA - flowRankB;
			const posA = positions[a] ?? nodePosition(graph.nodes[a]!);
			const posB = positions[b] ?? nodePosition(graph.nodes[b]!);
			if (posA.y !== posB.y) return posA.y - posB.y;
			if (posA.x !== posB.x) return posA.x - posB.x;
			return compareNodeIds(graph, a, b);
		});
	const placedDataNodes = new Set<string>();

	function placeSource(
		sourceId: string,
		targetId: string,
		siblingIndex: number,
		siblingCount: number,
		path: Set<string>,
	) {
		if (path.has(sourceId)) return;
		if (flowConnected.has(sourceId)) return;
		const targetPosition = positions[targetId];
		if (!targetPosition) return;
		const nextPath = new Set(path);
		nextPath.add(sourceId);
		if (!placedDataNodes.has(sourceId)) {
			const sourceNode = graph.nodes[sourceId];
			const sourceWidth = sourceNode ? nodeWidth(sourceNode) : GRAPH_LAYOUT_NODE_WIDTH;
			const siblingOffset =
				(siblingIndex - (siblingCount - 1) / 2)
				* (GRAPH_LAYOUT_NODE_HEIGHT + DATA_INPUT_STACK_GAP);
			if (direction === "vertical") {
				positions[sourceId] = {
					x: Math.round(targetPosition.x - sourceWidth - DATA_INPUT_GAP),
					y: Math.round(targetPosition.y + siblingOffset),
				};
			} else {
				positions[sourceId] = {
					x: Math.round(targetPosition.x + siblingOffset),
					y: Math.round(targetPosition.y - GRAPH_LAYOUT_NODE_HEIGHT - DATA_INPUT_GAP),
				};
			}
			placedDataNodes.add(sourceId);
		}
		const ownInputs = inputsByTarget.get(sourceId) ?? [];
		ownInputs.forEach((edge, index) => {
			placeSource(edge.source, sourceId, index, ownInputs.length, nextPath);
		});
	}

	for (const targetId of targets) {
		const inputs = inputsByTarget.get(targetId) ?? [];
		inputs.forEach((edge, index) => {
			placeSource(edge.source, targetId, index, inputs.length, new Set([targetId]));
		});
	}
}

export function computeActionGraphLayoutPositions(
	graph: ActionGraph,
	options: GraphLayoutOptions = {},
): Record<string, GraphLayoutPosition> {
	const nodeIds = Object.keys(graph.nodes).sort((a, b) => compareNodeIds(graph, a, b));
	if (nodeIds.length === 0) return {};

	const direction = options.direction ?? "vertical";
	const origin = options.origin ?? { x: 0, y: 0 };
	const nodeDepth =
		direction === "vertical" ? GRAPH_LAYOUT_NODE_HEIGHT : GRAPH_LAYOUT_NODE_WIDTH;
	const layoutEdges = buildLayoutEdges(graph);
	const incomingCount = new Map<string, number>();
	const outgoing = new Map<string, LayoutEdge[]>();

	for (const edge of layoutEdges) {
		incomingCount.set(edge.target, (incomingCount.get(edge.target) ?? 0) + 1);
		const edges = outgoing.get(edge.source) ?? [];
		edges.push(edge);
		outgoing.set(edge.source, edges);
	}
	for (const edges of outgoing.values()) {
		edges.sort((a, b) => compareEdges(graph, a, b));
	}

	const rootCandidates = nodeIds.filter((id) => {
		const node = graph.nodes[id];
		return Boolean(node && EVENT_ORDER.has(node.type));
	});
	for (const id of nodeIds) {
		if ((incomingCount.get(id) ?? 0) === 0 && !rootCandidates.includes(id)) {
			rootCandidates.push(id);
		}
	}

	const assigned = new Set<string>();
	const parentByNode = new Map<string, string>();
	const parentEdgeByNode = new Map<string, LayoutEdge>();

	function assignTree(rootId: string) {
		if (assigned.has(rootId)) return;
		assigned.add(rootId);
		const queue = [rootId];
		for (let index = 0; index < queue.length; index += 1) {
			const source = queue[index];
			if (!source) continue;
			for (const edge of outgoing.get(source) ?? []) {
				if (assigned.has(edge.target)) continue;
				assigned.add(edge.target);
				parentByNode.set(edge.target, source);
				parentEdgeByNode.set(edge.target, edge);
				queue.push(edge.target);
			}
		}
	}

	for (const rootId of rootCandidates) assignTree(rootId);
	for (const nodeId of nodeIds) assignTree(nodeId);

	const childrenByNode = new Map<string, string[]>();
	for (const [nodeId, parentId] of parentByNode) {
		const children = childrenByNode.get(parentId) ?? [];
		children.push(nodeId);
		childrenByNode.set(parentId, children);
	}
	for (const children of childrenByNode.values()) {
		children.sort((a, b) => {
			const edgeA = parentEdgeByNode.get(a);
			const edgeB = parentEdgeByNode.get(b);
			if (edgeA && edgeB) return compareEdges(graph, edgeA, edgeB);
			return compareNodeIds(graph, a, b);
		});
	}

	const roots = nodeIds
		.filter((id) => !parentByNode.has(id))
		.sort((a, b) => compareNodeIds(graph, a, b));
	const subtreeBreadth = new Map<string, number>();

	function measure(nodeId: string): number {
		const cached = subtreeBreadth.get(nodeId);
		if (cached !== undefined) return cached;
		const children = childrenByNode.get(nodeId) ?? [];
		if (children.length === 0) {
			const breadth = nodeBreadth(graph, nodeId, direction);
			subtreeBreadth.set(nodeId, breadth);
			return breadth;
		}
		const childrenBreadth =
			children.reduce((sum, childId) => sum + measure(childId), 0)
			+ GRAPH_LAYOUT_BRANCH_GAP * (children.length - 1);
		const breadth = Math.max(nodeBreadth(graph, nodeId, direction), childrenBreadth);
		subtreeBreadth.set(nodeId, breadth);
		return breadth;
	}

	const abstractPositions = new Map<string, { breadthCenter: number; depth: number }>();

	function place(nodeId: string, breadthStart: number, depth: number): number {
		const breadth = measure(nodeId);
		const children = childrenByNode.get(nodeId) ?? [];
		let breadthCenter = breadthStart + breadth / 2;
		if (children.length > 0) {
			const childrenBreadth =
				children.reduce((sum, childId) => sum + measure(childId), 0)
				+ GRAPH_LAYOUT_BRANCH_GAP * (children.length - 1);
			let childStart = breadthStart + (breadth - childrenBreadth) / 2;
			let firstCenter = 0;
			let lastCenter = 0;
			children.forEach((childId, index) => {
				const childCenter = place(childId, childStart, depth + 1);
				if (index === 0) firstCenter = childCenter;
				lastCenter = childCenter;
				childStart += measure(childId) + GRAPH_LAYOUT_BRANCH_GAP;
			});
			breadthCenter = (firstCenter + lastCenter) / 2;
		}
		abstractPositions.set(nodeId, { breadthCenter, depth });
		return breadthCenter;
	}

	let breadthStart = 0;
	for (const rootId of roots) {
		place(rootId, breadthStart, 0);
		breadthStart += measure(rootId) + GRAPH_LAYOUT_COMPONENT_GAP;
	}

	const positions: Record<string, GraphLayoutPosition> = {};
	for (const [nodeId, position] of abstractPositions) {
		const node = graph.nodes[nodeId];
		if (!node) continue;
		if (direction === "vertical") {
			positions[nodeId] = {
				x: Math.round(origin.x + position.breadthCenter - nodeWidth(node) / 2),
				y: Math.round(origin.y + position.depth * (nodeDepth + GRAPH_LAYOUT_LAYER_GAP)),
			};
		} else {
			positions[nodeId] = {
				x: Math.round(origin.x + position.depth * (nodeDepth + GRAPH_LAYOUT_LAYER_GAP)),
				y: Math.round(origin.y + position.breadthCenter - nodeHeight(node) / 2),
			};
		}
	}

	placeDataInputs(graph, positions, layoutEdges, direction);
	normalizePositions(positions, origin);

	return positions;
}

export function getNextGraphNodePosition(
	graph: ActionGraph,
	options: { origin?: GraphLayoutPosition } = {},
): GraphLayoutPosition {
	const nodes = Object.values(graph.nodes);
	const origin = options.origin ?? { x: 0, y: 0 };
	if (nodes.length === 0) return origin;
	const step = GRAPH_LAYOUT_NODE_HEIGHT + GRAPH_LAYOUT_LAYER_GAP;
	let minX = Number.POSITIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;
	const occupied = new Set<string>();
	for (const node of nodes) {
		const position = nodePosition(node);
		minX = Math.min(minX, position.x);
		maxY = Math.max(maxY, position.y);
		occupied.add(`${Math.round(position.x)}:${Math.round(position.y)}`);
	}
	const x = Number.isFinite(minX) ? minX : origin.x;
	let y = Number.isFinite(maxY) ? maxY + step : origin.y;
	while (occupied.has(`${Math.round(x)}:${Math.round(y)}`)) y += step;
	return { x, y };
}

export function beautifyActionGraph(
	graph: ActionGraph,
	options: GraphLayoutOptions = {},
): ActionGraph {
	const positions = computeActionGraphLayoutPositions(graph, options);
	const nodes: ActionGraph["nodes"] = {};
	for (const [nodeId, node] of Object.entries(graph.nodes)) {
		nodes[nodeId] = {
			...node,
			position: positions[nodeId] ?? node.position,
		} as GraphNode;
	}

	const result: ActionGraph = {
		...graph,
		nodes,
		edges: [...graph.edges],
	};
	if (graph.comments) {
		result.comments = options.layoutComments === false
			? [...graph.comments]
			: graph.comments.map((comment) => {
				const children = Object.values(nodes).filter((node) => node.parentId === comment.id);
				if (children.length === 0) return { ...comment };
				let minX = Number.POSITIVE_INFINITY;
				let minY = Number.POSITIVE_INFINITY;
				let maxX = Number.NEGATIVE_INFINITY;
				let maxY = Number.NEGATIVE_INFINITY;
				for (const child of children) {
					const position = nodePosition(child);
					minX = Math.min(minX, position.x);
					minY = Math.min(minY, position.y);
					maxX = Math.max(maxX, position.x + nodeWidth(child));
					maxY = Math.max(maxY, position.y + nodeHeight(child));
				}
				return {
					...comment,
					position: {
						x: Math.round(minX - COMMENT_PADDING),
						y: Math.round(minY - COMMENT_PADDING - COMMENT_HEADER_HEIGHT),
					},
					size: {
						width: Math.round(maxX - minX + COMMENT_PADDING * 2),
						height: Math.round(maxY - minY + COMMENT_PADDING * 2 + COMMENT_HEADER_HEIGHT),
					},
				};
			});
	}
	return result;
}
